import { Link } from "@tanstack/react-router";
import { ArrowLeft, Flag, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { MilestonePhase, MilestoneTemplateItem } from "@/hooks/use-milestones";
import {
  useCreateMilestoneTemplateItem,
  useDeleteMilestoneTemplateItem,
  useMilestoneTemplateItems,
  useUpdateMilestoneTemplateItem,
} from "@/hooks/use-milestones";

const PHASES: { value: MilestonePhase; label: string }[] = [
  { value: "pre_construction", label: "Pre-Construction" },
  { value: "construction", label: "Construction" },
  { value: "post_construction", label: "Post-Construction" },
];

function phaseLabel(phase: MilestonePhase) {
  return PHASES.find((p) => p.value === phase)?.label ?? phase;
}

function phaseBadgeVariant(phase: MilestonePhase): "default" | "secondary" | "outline" {
  if (phase === "pre_construction") return "outline";
  if (phase === "construction") return "default";
  return "secondary";
}

// ── Create / Edit Dialog ──

function MilestoneTemplateDialog({
  item,
  open,
  onOpenChange,
}: {
  item: MilestoneTemplateItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateMilestoneTemplateItem();
  const update = useUpdateMilestoneTemplateItem();

  const isEdit = item !== null;
  const [name, setName] = useState(item?.name ?? "");
  const [phase, setPhase] = useState<MilestonePhase>(item?.phase ?? "pre_construction");
  const [sortOrder, setSortOrder] = useState(String(item?.sortOrder ?? 0));
  const [linkedTaskName, setLinkedTaskName] = useState(item?.linkedTaskName ?? "");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      name,
      phase,
      sortOrder: Number(sortOrder),
      linkedTaskName: linkedTaskName || null,
    };
    if (isEdit && item) {
      update.mutate({ id: item.id, ...payload }, { onSuccess: () => onOpenChange(false) });
    } else {
      create.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  }

  const isPending = isEdit ? update.isPending : create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Milestone" : "Add Milestone"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ms-name">Name</Label>
            <Input id="ms-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ms-phase">Phase</Label>
              <Select value={phase} onValueChange={(v) => setPhase(v as MilestonePhase)}>
                <SelectTrigger id="ms-phase">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PHASES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ms-order">Sort Order</Label>
              <Input
                id="ms-order"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ms-task">Linked Task Name (optional)</Label>
            <Input
              id="ms-task"
              value={linkedTaskName}
              onChange={(e) => setLinkedTaskName(e.target.value)}
              placeholder="e.g. Permit Issuance"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Add Milestone"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Phase Group ──

function PhaseGroup({
  phase,
  items,
  onEdit,
}: {
  phase: MilestonePhase;
  items: MilestoneTemplateItem[];
  onEdit: (item: MilestoneTemplateItem) => void;
}) {
  const deleteItem = useDeleteMilestoneTemplateItem();

  if (items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Badge variant={phaseBadgeVariant(phase)}>{phaseLabel(phase)}</Badge>
          <span className="text-muted-foreground font-normal">({items.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-24">Sort Order</TableHead>
              <TableHead>Linked Task</TableHead>
              <TableHead className="w-24">System</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell>{item.sortOrder}</TableCell>
                <TableCell className="text-muted-foreground">
                  {item.linkedTaskName ?? "--"}
                </TableCell>
                <TableCell>
                  {item.isSystemDefault ? (
                    <Badge variant="outline" className="text-xs">
                      System
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onEdit(item)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => deleteItem.mutate(item.id)}
                      disabled={deleteItem.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ── Main Page ──

export function MilestoneTemplatesPage() {
  const { data, isLoading, isError, error, refetch } = useMilestoneTemplateItems();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MilestoneTemplateItem | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  const items = data ?? [];

  const byPhase = (phase: MilestonePhase) =>
    items.filter((i) => i.phase === phase).sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Milestone Templates"
        description="Define the default milestones applied to every new project"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/config">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Configuration
              </Link>
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Milestone
            </Button>
          </div>
        }
      />

      {items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Flag className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold">No milestone templates yet</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Add milestones to define the standard timeline for all projects.
            </p>
            <Button className="mt-4" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Milestone
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {PHASES.map((p) => (
            <PhaseGroup
              key={p.value}
              phase={p.value}
              items={byPhase(p.value)}
              onEdit={setEditingItem}
            />
          ))}
        </div>
      )}

      <MilestoneTemplateDialog item={null} open={createOpen} onOpenChange={setCreateOpen} />
      {editingItem ? (
        <MilestoneTemplateDialog
          item={editingItem}
          open={true}
          onOpenChange={(o) => {
            if (!o) setEditingItem(null);
          }}
        />
      ) : null}
    </div>
  );
}
